<template>
  <div class="ts">
    <div v-if="!t" class="w-empty"><i class="fas fa-boxes"></i>Loading…</div>
    <template v-else>
      <div class="ts-ring">
        <svg viewBox="0 0 120 120">
          <circle class="ts-track" cx="60" cy="60" r="50"/>
          <circle class="ts-fill" cx="60" cy="60" r="50" :style="{ strokeDashoffset: offset }"/>
        </svg>
        <div class="ts-center">
          <div class="ts-big">{{ fmt(usedPct, 1) }}%</div>
          <div class="ts-small">of the splits delivered</div>
        </div>
      </div>
      <div class="ts-bar">
        <span v-for="s in segments" :key="s.key" :style="{ flexGrow: s.value, background: s.color }" :title="`${s.label}: ${fmt(s.value)} trees`"></span>
      </div>
      <div class="ts-legend">
        <div v-for="s in segments" :key="s.key" class="ts-item">
          <span class="ts-dot" :style="{ background: s.color }"></span>
          <span>{{ s.label }}</span>
          <b>{{ fmt(s.value) }}</b>
        </div>
      </div>
      <div class="ts-foot">{{ fmt(t.splitsDelivered) }} of {{ compact(t.splitsTotal) }} splits delivered · {{ fmt(t.total) }} trees</div>
    </template>
  </div>
</template>

<script>
import { compact, fmt, palette, themeState } from '../theme';

const CIRC = 2 * Math.PI * 50;

export default {
  name: 'TreeStock',
  props: { stats: { type: Object, required: true } },
  data() { return { shown: false }; },
  computed: {
    t() { return (this.stats.insights && this.stats.insights.trees) || null; },
    usedPct() { return this.t && this.t.splitsTotal ? (this.t.splitsDelivered / this.t.splitsTotal) * 100 : 0; },
    offset() { return this.shown ? CIRC * (1 - Math.min(1, Math.max(0.01, this.usedPct / 100))) : CIRC; },
    segments() {
      void themeState.tick;
      const p = palette();
      const t = this.t || {};
      return [
        { key: 'free', label: 'Free for splits', value: t.free || 0, color: p.green },
        { key: 'assigned', label: 'Assigned to a company', value: t.assigned || 0, color: p.teal },
        { key: 'used', label: 'In use (splits delivered)', value: t.used || 0, color: p.amber },
        { key: 'whole', label: 'Whole trees (NFT)', value: t.wholeTrees || 0, color: p.violet },
      ];
    }
  },
  watch: { t(v) { if (v) this.reveal(); } },
  mounted() { if (this.t) this.reveal(); },
  methods: {
    fmt,
    compact,
    reveal() { setTimeout(() => { this.shown = true; }, 150); }
  }
};
</script>

<style scoped lang="scss">
@import '../widgets.scss';

.ts {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 16px 12px;
  gap: 10px;
}

.ts-ring {
  position: relative;
  width: min(150px, 60%);
  aspect-ratio: 1;
  flex: 0 1 auto;

  svg { width: 100%; height: 100%; transform: rotate(-90deg); }
}

.ts-track { fill: none; stroke: var(--bg-secondary); stroke-width: 11; }
.ts-fill {
  fill: none;
  stroke: var(--accent-green);
  stroke-width: 11;
  stroke-linecap: round;
  stroke-dasharray: 314.16;
  transition: stroke-dashoffset 1.8s cubic-bezier(.2, .8, .2, 1);
  filter: drop-shadow(0 0 6px rgba(var(--accent-green-rgb), 0.5));
}

.ts-center {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.ts-big { font-family: 'Sora', sans-serif; font-weight: 800; font-size: 1.35rem; color: var(--text-heading); }
.ts-small { font-size: 0.68rem; color: var(--text-muted); max-width: 90px; line-height: 1.2; }

.ts-bar {
  display: flex;
  width: 100%;
  height: 12px;
  border-radius: 8px;
  overflow: hidden;
  gap: 2px;

  span { min-width: 3px; animation: ts-in 1.2s cubic-bezier(.2, .8, .2, 1); transform-origin: left; }
}

@keyframes ts-in { from { transform: scaleX(0); } to { transform: scaleX(1); } }

.ts-legend {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 12px;
}

.ts-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.74rem;
  color: var(--text-muted);

  b { margin-left: auto; color: var(--text-heading); font-variant-numeric: tabular-nums; }
}

.ts-dot { width: 9px; height: 9px; border-radius: 3px; flex: 0 0 9px; }
.ts-foot { font-size: 0.7rem; color: var(--text-muted); }
</style>
